import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import { MatchStatus, ReconFilters } from "../types";

export const EMPTY_FILTERS: ReconFilters = {
  products: [],
  matches: [],
  priceMin: "",
  priceMax: "",
  amountMin: "",
  amountMax: "",
  includeReversed: false,
};

const MATCH_LABELS: Record<MatchStatus, string> = {
  matched: "Matched",
  mismatched: "Mismatched",
  pending: "Pending",
};

interface Props {
  products: string[];
  value: ReconFilters;
  onChange: (next: ReconFilters) => void;
  onClose: () => void;
}

const FilterPopover: React.FC<Props> = ({
  products,
  value,
  onChange,
  onClose,
}) => {
  const [local, setLocal] = useState<ReconFilters>(value);
  const [prodQuery, setProdQuery] = useState("");
  const popRef = useRef<HTMLDivElement>(null);

  const visibleProducts = useMemo(
    () =>
      products.filter((product) =>
        product.toLowerCase().includes(prodQuery.toLowerCase()),
      ),
    [products, prodQuery],
  );

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(event.target as Node))
        onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const toggleProduct = (item: string): void => {
    const next = local.products.includes(item)
      ? local.products.filter((entry) => entry !== item)
      : [...local.products, item];
    setLocal({ ...local, products: next });
  };
  const toggleMatch = (item: MatchStatus): void => {
    const next = local.matches.includes(item)
      ? local.matches.filter((entry) => entry !== item)
      : [...local.matches, item];
    setLocal({ ...local, matches: next });
  };

  const apply = (): void => {
    onChange(local);
    onClose();
  };
  const reset = (): void => setLocal(EMPTY_FILTERS);

  return (
    <div className="filter-pop" ref={popRef}>
      <div className="filter-pop-head">
        <div className="filter-pop-title">Filters</div>
        <button className="filter-pop-reset" onClick={reset}>
          Reset all
        </button>
      </div>

      <div className="filter-pop-body">
        <div className="filter-group">
          <div className="filter-group-label">Products</div>
          <div className="multi-select">
            <div className="multi-select-tags">
              {local.products.map((product) => (
                <span key={product} className="ms-tag">
                  {product}
                  <button
                    onClick={() => toggleProduct(product)}
                    aria-label={`Remove ${product}`}
                  >
                    <Icon name="x" size={11} strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
            <div className="multi-select-search">
              <Icon name="search" size={13} />
              <input
                placeholder="Search products…"
                value={prodQuery}
                onChange={(event) => setProdQuery(event.target.value)}
              />
            </div>
            <div className="multi-select-list">
              {!prodQuery.trim() ? (
                <div className="ms-empty">Start typing to search products…</div>
              ) : visibleProducts.length === 0 ? (
                <div className="ms-empty">
                  No products match &quot;{prodQuery}&quot;
                </div>
              ) : (
                visibleProducts.map((product) => (
                  <label key={product} className="ms-option">
                    <input
                      type="checkbox"
                      checked={local.products.includes(product)}
                      onChange={() => toggleProduct(product)}
                    />
                    <span>{product}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-label">Match status</div>
          <div className="filter-checks row">
            {(Object.entries(MATCH_LABELS) as [MatchStatus, string][]).map(
              ([key, label]) => (
                <label key={key} className="filter-check">
                  <input
                    type="checkbox"
                    checked={local.matches.includes(key)}
                    onChange={() => toggleMatch(key)}
                  />
                  <span>{label}</span>
                </label>
              ),
            )}
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-label">Price range</div>
          <div className="filter-range">
            <input
              className="input input-sm filter-range-input"
              placeholder="Min"
              value={local.priceMin}
              onChange={(event) =>
                setLocal({ ...local, priceMin: event.target.value })
              }
            />
            <span className="filter-dash">–</span>
            <input
              className="input input-sm filter-range-input"
              placeholder="Max"
              value={local.priceMax}
              onChange={(event) =>
                setLocal({ ...local, priceMax: event.target.value })
              }
            />
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-group-label">Total amount range (GHS)</div>
          <div className="filter-range">
            <input
              className="input input-sm filter-range-input"
              placeholder="Min"
              value={local.amountMin}
              onChange={(event) =>
                setLocal({ ...local, amountMin: event.target.value })
              }
            />
            <span className="filter-dash">–</span>
            <input
              className="input input-sm filter-range-input"
              placeholder="Max"
              value={local.amountMax}
              onChange={(event) =>
                setLocal({ ...local, amountMax: event.target.value })
              }
            />
          </div>
        </div>
      </div>

      <div className="filter-pop-foot">
        <button className="btn btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={apply}>
          Apply filters
        </button>
      </div>
    </div>
  );
};

export default FilterPopover;
export { MATCH_LABELS };
